import { LegalEntityCountry } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { ComponentProps, useState } from 'react';
import { AccountView, BeneficiaryView } from '../../../../types/entity-types';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { BeneficiaryListFlow } from '../../beneficiary-list-flow/beneficiary-list-flow';
import { PaymentDetails } from '../../payment-flow-storage';
import { usePaymentFlow } from '../../use-payment-flow';
import { InvoiceDetailsWithOptionalCurrency } from './invoice-payment-information-without-toggle';
import { KnownBeneficiaryFlow } from './known-beneficiary-flow';
import { UnknownBeneficiaryCreateNewFlow } from './unknown-beneficiary-create-new-flow';
import { UnsupportedInvoiceDetails } from './unsupported-invoice-details';

type Step = 'ALL_BENEFICIARIES' | 'CREATE_BENEFICIARY' | 'KNOWN_BENEFICIARY';

type UnknownBeneficiaryFlowProps = {
  beneficiaries: BeneficiaryView[];
  companyCountry: LegalEntityCountry;
  accounts: AccountView[];
  invoiceDetails: InvoiceDetailsWithOptionalCurrency;
  isLoading: boolean;
  onGoBack: () => void;
  onCreateBeneficiary: ComponentProps<typeof UnknownBeneficiaryCreateNewFlow>['onCreateBeneficiary'];
  onMakePayment: (paymentDetails: PaymentDetails) => Promise<void>;
};

export const UnknownBeneficiaryFlow = ({
  beneficiaries,
  companyCountry,
  accounts,
  invoiceDetails,
  isLoading,
  onGoBack,
  onCreateBeneficiary,
  onMakePayment,
}: UnknownBeneficiaryFlowProps) => {
  const {
    updateSelectedBeneficiaryId,
    getSelectedBeneficiaryId,
    shouldRenderReviewStep,
    clearSelectedBeneficiaryId,
    clearPaymentDetails,
  } = usePaymentFlow();
  const [step, setStep] = useState<Step>(shouldRenderReviewStep ? 'KNOWN_BENEFICIARY' : 'ALL_BENEFICIARIES');

  switch (step) {
    case 'ALL_BENEFICIARIES': {
      const filteredBeneficiaries = invoiceDetails.currency
        ? // If we have an invoice currency, we should only display beneficiaries that match the currency of the invoice
          beneficiaries.filter((beneficiary) => beneficiary.destination.currency === invoiceDetails.currency)
        : // Otherwise, we display all beneficiaries because we don't know the currency of the invoice so the user will choose the currency
          beneficiaries;

      return (
        <BeneficiaryListFlow
          beneficiaries={filteredBeneficiaries}
          isWarningCardVisible={true}
          onGoBack={onGoBack}
          onCreateBeneficiary={() => setStep('CREATE_BENEFICIARY')}
          onSelectBeneficiary={(beneficiaryId) => {
            updateSelectedBeneficiaryId(beneficiaryId);

            setStep('KNOWN_BENEFICIARY');
          }}
        />
      );
    }
    case 'CREATE_BENEFICIARY':
      return (
        <UnknownBeneficiaryCreateNewFlow
          isLoading={isLoading}
          companyCountry={companyCountry}
          accounts={accounts}
          beneficiaries={beneficiaries}
          invoiceDetails={invoiceDetails}
          onCreateBeneficiary={onCreateBeneficiary}
          onMakePayment={onMakePayment}
          onCancel={() => setStep('ALL_BENEFICIARIES')}
        />
      );

    case 'KNOWN_BENEFICIARY': {
      const selectedBeneficiaryId = getSelectedBeneficiaryId();

      if (!selectedBeneficiaryId) {
        Sentry.captureMessage('No beneficiary selected in KNOWN_BENEFICIARY step in UNKNOWN_BENEFICIARY flow', {
          extra: {
            selectedBeneficiaryId,
            beneficiaryIds: beneficiaries.map((beneficiary) => beneficiary.id),
            step,
            message: 'No beneficiary selected in KNOWN_BENEFICIARY step in UNKNOWN_BENEFICIARY flow',
          },
        });

        return (
          <UnsupportedInvoiceDetails
            title="Oups, something went wrong"
            variantConfig={{ variant: 'GENERIC_ERROR' }}
            onGoBack={onGoBack}
          />
        );
      }

      const selectedBeneficiary = beneficiaries.find((beneficiary) => beneficiary.id === selectedBeneficiaryId);

      if (!selectedBeneficiary) {
        Sentry.captureMessage(
          'Selected beneficiary cannot be found among the list of beneficiaries in KNOWN_BENEFICIARY step in UNKNOWN_BENEFICIARY flow',
          {
            extra: {
              selectedBeneficiaryId,
              beneficiaryIds: beneficiaries.map((beneficiary) => beneficiary.id),
              step,
              message:
                'Selected beneficiary cannot be found among the list of beneficiaries in KNOWN_BENEFICIARY step in UNKNOWN_BENEFICIARY flow',
            },
          },
        );

        return (
          <UnsupportedInvoiceDetails
            title="Oups, something went wrong"
            variantConfig={{ variant: 'GENERIC_ERROR' }}
            onGoBack={onGoBack}
          />
        );
      }

      const beneficiaryCurrency = selectedBeneficiary.destination.currency;
      const beneficiariesWithMatchingCurrency = beneficiaries.filter(
        (beneficiary) => beneficiary.destination.currency === beneficiaryCurrency,
      );

      return (
        <KnownBeneficiaryFlow
          accounts={accounts}
          beneficiaries={beneficiariesWithMatchingCurrency}
          // We add the beneficiary that the user selected so the flow can work well
          initialInvoiceDetails={{
            ...invoiceDetails,
            currency: beneficiaryCurrency,
            suggestedBeneficiaryId: selectedBeneficiaryId,
          }}
          isLoading={isLoading}
          onMakePayment={onMakePayment}
          onCancelTransfer={() => {
            clearSelectedBeneficiaryId();
            clearPaymentDetails();

            setStep('ALL_BENEFICIARIES');
          }}
        />
      );
    }
    default:
      return exhaustiveCheck(step);
  }
};
